import { useState } from 'react';
import { Button, Fieldset, Flex, Grid, GridArea, Heading } from '../../components';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useToast } from '../../hooks';
import CountyReportForm from './CountyReportForm';

function RosterReportPage() {
  const [dirty, setDirty] = useState(false);
  const [memberTypeId, setMemberTypeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [countyIds, setCountyIds] = useState<string[]>([]);
  const [enrollmentIds, setEnrollmentIds] = useState<string[]>([]);
  const toaster = useToast();

  const [generateReport] = useMutation(gql`
    mutation CreatePreAuthCountyReportLink($format: String!) {
      url: preAuthMemberTypeCountyReportUrl(format: $format)
    }
  `);

  const handleMemberTypeChange = (id: string | null) => {
    setMemberTypeId(id);
    setDirty(true);
  };

  const handleCountyChange = (ids: string[]) => {
    setCountyIds(ids);
    setDirty(true);
  };

  const handleEnrollmentChange = (ids: string[]) => {
    setEnrollmentIds(ids);
    setDirty(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (memberTypeId === null) {
      toaster('Please select a member type', { context: 'warning' });
      return;
    }

    if (countyIds.length === 0) {
      toaster('Please select at least one county', { context: 'warning' });
      return;
    }

    setDirty(false);
    setLoading(true);

    try {
      const result = await generateReport({ variables: { format: 'xlsx' } });

      if (result.data?.url) {
        // After getting the pre-auth URL, we need to make a POST request with the selected IDs
        const fullUrl = `${process.env.REACT_APP_GRAPH_HOST}${result.data.url}`;

        const payload = {
          memberType: memberTypeId,
          counties: countyIds,
          excludedEnrollments: enrollmentIds,
        };

        // Make the POST request to download the file
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          // Create a blob from the response and trigger download
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = 'county-report.xlsx';
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(downloadUrl);

          toaster('Report downloaded successfully', { context: 'primary' });
        } else {
          const errorText = await response.text();
          throw new Error(`API Error: ${errorText}`);
        }
      }
    } catch (e: any) {
      console.error(e.message);
      toaster(`Failed to generate the report. ${e.message}`, {
        context: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Heading>Roster Report</Heading>
      <Fieldset onChange={_ => setDirty(true)}>
        <Grid
          template={`
            "reportForm reportForm reportForm"
            "actions . ."
            /1fr 1fr 80px`}
          gap={16}
        >
          <GridArea area="reportForm">
            <CountyReportForm
              onMemberTypeChange={handleMemberTypeChange}
              onCountyChange={handleCountyChange}
              onEnrollmentChange={handleEnrollmentChange}
            />
          </GridArea>
          <GridArea area="actions">
            <form onSubmit={handleSubmit}>
              <Flex>
                <Button type="submit" disabled={loading || !dirty}>
                  {loading ? 'Generating...' : 'Generate & Download'}
                </Button>
              </Flex>
            </form>
          </GridArea>
        </Grid>
      </Fieldset>
    </div>
  );
}

export default RosterReportPage;
